import React, { Component } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { FileUpload } from 'primereact/fileupload';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';
import { ProgressBar } from 'primereact/progressbar';
import { Badge } from 'primereact/badge';
import { Panel } from 'primereact/panel';
import { TabView, TabPanel } from 'primereact/tabview';
import { Toolbar } from 'primereact/toolbar';
import { SplitButton } from 'primereact/splitbutton';
import { InputTextarea } from 'primereact/inputtextarea';
import { Checkbox } from 'primereact/checkbox';
import { Row, Col } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import JSZip from 'jszip';
import { withTranslation } from 'react-i18next';
import CarteV2Service from '../../../../services/CarteV2Service';
import { ProgressSpinner } from 'primereact/progressspinner';

class GenerationBatch extends Component {
    constructor(props) {
        super(props);
        this.state = {
            // Onglets
            activeTabIndex: 0,

            // Import de fichier
            importMethod: 'creation', // 'search', 'creation' (removed 'file' as it's redundant)
            uploadedFile: null,
            importedBeneficiaries: [],
            validationErrors: [],

            // Création par lot
            creationData: [],
            creationValidationErrors: [],

            // Bulk photo upload
            showPhotoMatchingDialog: false,
            photoMatchingResults: {
                matched: [],
                unmatched: [],
                orphanPhotos: []
            },
            bulkPhotoProcessing: false,

            // Recherche par critères
            searchCriteria: {
                police: '',
                nom: '',
                famille: '',
                status: 'active'
            },
            searchResults: [],

            // Sélection manuelle
            manualSelection: [],
            selectedBeneficiaries: [],

            // Configuration du batch
            batchConfig: {
                name: '',
                description: '',
                templateId: null,
                stockId: null,
                priority: 'Normal',
                country: 'LB',
                generatePdf: true,
                autoValidate: false
            },

            // Stocks disponibles
            stocks: [],
            stocksLoading: false,

            // Génération
            isGenerating: false,
            generationProgress: 0,
            currentBatch: null,
            generationStats: {
                total: 0,
                success: 0,
                errors: 0,
                inProgress: 0
            },

            // Données
            templates: [],
            countries: [],
            priorities: [],
            statusOptions: [],

            // Historique des batches
            batchHistory: [],

            // Dialogs
            showPreviewDialog: false,
            showValidationDialog: false,
            showErrorsDialog: false,

            // Loading states
            templatesLoading: false,
            searchLoading: false,
            historyLoading: false,
            isPdfDownloading: false,
            downloadProgress: 0,
            previewLoading: false,
            previewUrl: null
        };

        this.toast = React.createRef();
    }

    componentDidMount() {
        this.initializeTranslatedOptions();
        this.loadTemplates();
        this.loadStocks();
        this.loadBatchHistory();
    }

    initializeTranslatedOptions = () => {
        const { t } = this.props;

        this.setState({
            countries: [
                { label: t('cardGeneration.batch.countries.liberia'), value: 'LB' },
                { label: t('cardGeneration.batch.countries.cameroon'), value: 'CM' },
                { label: t('cardGeneration.batch.countries.ivoryCoast'), value: 'CI' }
            ],
            priorities: [
                { label: t('cardGeneration.batch.priority.normal'), value: 'Normal' },
                { label: t('cardGeneration.batch.priority.high'), value: 'Urgent' },
                { label: t('cardGeneration.batch.priority.urgent'), value: 'Express' }
            ],
            statusOptions: [
                { label: t('cardGeneration.batch.badges.active'), value: 'active' },
                { label: t('cardGeneration.batch.badges.inactive'), value: 'inactive' },
                { label: t('cardGeneration.batch.labels.total'), value: 'all' }
            ]
        });
    }

    showToast = (severity, summary, detail) => {
        this.toast.current.show({ severity, summary, detail });
    };

    // ===== CHARGEMENT DES DONNÉES =====

    loadTemplates = async () => {
        const { t } = this.props;
        this.setState({ templatesLoading: true });
        try {
            const templates = await CarteV2Service.getTemplates({ isActive: true });
            this.setState({ templates });
        } catch (error) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.templatesLoadFailed'));
        } finally {
            this.setState({ templatesLoading: false });
        }
    };

    loadStocks = async () => {
        const { t } = this.props;
        this.setState({ stocksLoading: true });
        try {
            const stocks = await CarteV2Service.getAvailableStocks();
            this.setState({ stocks });
        } catch (error) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.stocksLoadFailed'));
        } finally {
            this.setState({ stocksLoading: false });
        }
    };

    loadBatchHistory = async () => {
        const { t } = this.props;
        this.setState({ historyLoading: true });
        try {
            const batches = await CarteV2Service.getCardBatches({
                pageNumber: 1,
                pageSize: 50
            });

            // Map API response to table format (handle both PascalCase and camelCase)
            const mappedBatches = batches.map(batch => ({
                id: batch.Id || batch.id,
                name: batch.BatchNumber || batch.batchNumber || `Batch ${batch.Id || batch.id}`,
                country: batch.CountryCode || batch.countryCode,
                totalCards: batch.TotalCards || batch.totalCards || 0,
                successCards: batch.CompletedCards || batch.completedCards || 0,
                errorCards: batch.FailedCards || batch.failedCards || 0,
                pendingCards: batch.PendingCards || batch.pendingCards || 0,
                status: (batch.Status || batch.status || '').toLowerCase().replace(/\s+/g, '_'),
                createdAt: batch.CreatedAt || batch.createdAt || batch.ProductionDate || batch.productionDate,
                printerOperator: batch.PrinterOperator || batch.printerOperator,
                notes: batch.Notes || batch.notes
            }));

            this.setState({ batchHistory: mappedBatches });
        } catch (error) {
            console.error('Erreur chargement historique:', error);
            this.showToast('error', t('common.error'), t('cardGeneration.batch.messages.errorLoadHistory'));
        } finally {
            this.setState({ historyLoading: false });
        }
    };

    // ===== IMPORT DE FICHIER =====

    handleFileUpload = (event) => {
        const { t } = this.props;
        const file = event.files[0];
        if (!file) return;

        // Validation du fichier
        const allowedTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
        ];

        if (!allowedTypes.includes(file.type)) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.warnings.fileTypeNotSupported'));
            return;
        }

        this.setState({ uploadedFile: file });
        this.parseFile(file);
    };

    downloadTemplate = () => {
        const { t } = this.props;
        // Create CSV template
        const csvContent = [
            ['numeroAssure', 'nom', 'prenom', 'police', 'famille', 'dateNaissance', 'email', 'telephone'],
            ['123456', 'Micheal', 'Jean', '2001120789', '1001', '1985-05-15', 'jean.dupont@example.com', '+237123456789'],
            ['789123', 'Samuel', 'Marie', '2001120456', '1002', '1990-08-22', 'marie.martin@example.com', '+225987654321']
        ];

        const csv = csvContent.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `template_import_batch_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.templateDownloaded'));
    };

    // Fonction pour convertir un numéro de série Excel en date
    excelDateToJSDate = (serial) => {
        // Excel stocke les dates comme nombre de jours depuis 1900-01-01
        // Mais il y a un bug dans Excel qui considère 1900 comme année bissextile
        if (typeof serial === 'number' && serial > 0) {
            const utc_days = Math.floor(serial - 25569);
            const utc_value = utc_days * 86400;
            const date_info = new Date(utc_value * 1000);

            // Formater en YYYY-MM-DD
            const year = date_info.getUTCFullYear();
            const month = String(date_info.getUTCMonth() + 1).padStart(2, '0');
            const day = String(date_info.getUTCDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        }
        return serial;
    };

    parseFile = async (file) => {
        const { t } = this.props;
        try {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array', cellDates: true });

                    // Lire la première feuille
                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    // Convertir en JSON avec option pour garder les dates
                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'yyyy-mm-dd' });

                    if (jsonData.length === 0) {
                        this.showToast('warn', t('common.warning'), t('cardGeneration.batch.warnings.fileEmpty'));
                        return;
                    }

                    // Valider et transformer les données
                    const processedData = jsonData.map((row, index) => {
                        const errors = [];

                        // Récupérer les champs (gérer différentes variantes de nommage)
                        const numeroAssure = row.numeroAssure || row.NumeroAssure || row.numero_assure || row['N° Assuré'] || '';
                        const nom = row.nom || row.Nom || row.lastName || row.LastName || '';
                        const prenom = row.prenom || row.Prenom || row.firstName || row.FirstName || '';
                        const police = row.police || row.Police || row.numeroPolice || row.NumeroPolice || row['N° Police'] || '';
                        const famille = row.famille || row.Famille || row.familyId || row.FamilyId || row['ID Famille'] || '';
                        let dateNaissance = row.dateNaissance || row.DateNaissance || row.birthdate || row.Birthdate || row['Date Naissance'] || '';
                        const email = row.email || row.Email || '';
                        const telephone = row.telephone || row.Telephone || row.phone || row.Phone || '';

                        // Convertir la date si c'est un numéro Excel
                        if (typeof dateNaissance === 'number') {
                            dateNaissance = this.excelDateToJSDate(dateNaissance);
                        } else if (dateNaissance) {
                            dateNaissance = dateNaissance.toString().trim();
                        }

                        // Validation des champs requis
                        if (!numeroAssure || numeroAssure.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.insuredNumberMissing'));
                        }
                        if (!nom || nom.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.lastNameMissing'));
                        }
                        if (!prenom || prenom.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.firstNameMissing'));
                        }
                        if (!police || police.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.policeNumberMissing'));
                        }

                        return {
                            id: index + 1, // Index pour affichage uniquement
                            idClient: numeroAssure.toString().trim(), // Vrai ID client
                            numeroAssure: numeroAssure.toString().trim(),
                            nom: nom.toString().trim(),
                            prenom: prenom.toString().trim(),
                            police: police.toString().trim(),
                            famille: famille ? famille.toString().trim() : '',
                            dateNaissance: dateNaissance || '',
                            email: email ? email.toString().trim() : '',
                            telephone: telephone ? telephone.toString().trim() : '',
                            status: errors.length === 0 ? 'valid' : 'error',
                            errors: errors
                        };
                    });

                    // Séparer les données valides et invalides
                    const validBeneficiaries = processedData.filter(b => b.status === 'valid');
                    const invalidBeneficiaries = processedData.filter(b => b.status === 'error');

                    this.setState({
                        importedBeneficiaries: validBeneficiaries,
                        validationErrors: invalidBeneficiaries,
                        selectedBeneficiaries: validBeneficiaries
                    });

                    this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.imported', { count: validBeneficiaries.length }));

                    if (invalidBeneficiaries.length > 0) {
                        this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.linesWithErrors', { count: invalidBeneficiaries.length }));
                    }
                } catch (parseError) {
                    console.error('Erreur parsing:', parseError);
                    this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.fileParsing') + ': ' + parseError.message);
                }
            };

            reader.onerror = () => {
                this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.fileReading'));
            };

            // Lire le fichier
            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error('Erreur parseFile:', error);
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.fileProcessing') + ': ' + error.message);
        }
    };

    // ===== RECHERCHE PAR CRITÈRES =====

    searchByCriteria = async () => {
        const { t } = this.props;
        const { searchCriteria } = this.state;

        // Validation - au moins un critère requis
        if (!searchCriteria.police && !searchCriteria.nom && !searchCriteria.famille) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.warnings.searchCriteriaRequired'));
            return;
        }

        this.setState({ searchLoading: true });
        try {
            const criteriaPayload = {
                police: searchCriteria.police,
                lastName: searchCriteria.nom,
                famille: searchCriteria.famille,
                status: searchCriteria.status
            };

            console.log('Recherche avec critères:', criteriaPayload);
            const response = await CarteV2Service.searchBeneficiariesByCriteria(criteriaPayload);
            console.log('API Response:', response);

            // Extraire les données - gérer différents formats de réponse
            let dataArray = [];
            if (Array.isArray(response)) {
                dataArray = response;
            } else if (response.data && Array.isArray(response.data)) {
                dataArray = response.data;
            } else if (response.items && Array.isArray(response.items)) {
                dataArray = response.items;
            } else if (response.results && Array.isArray(response.results)) {
                dataArray = response.results;
            }

            console.log('Data array extracted:', dataArray);

            // Adapter les données - gérer tous les cas de nommage
            const adaptedResults = dataArray.map((ben, index) => {
                // Extraire les données avec fallbacks pour différentes structures
                const personalInfo = ben.personalInfo || ben.PersonalInfo || ben;
                const status = ben.status || ben.Status || {};
                const insuranceCards = ben.insuranceCards || ben.InsuranceCards || [];

                // IMPORTANT: idClient est le vrai ID du client (personalInfo.idClient)
                // id est l'ID de l'enregistrement dans la table (personalInfo.id)
                const idClient = personalInfo.idClient || personalInfo.IdClient ||
                                personalInfo.id || personalInfo.Id || `ben-${index}`;

                // Vérifier si le bénéficiaire a une carte active
                const hasActiveCard = status.hasValidCard || status.HasValidCard ||
                                     insuranceCards.length > 0 || false;

                const result = {
                    id: personalInfo.id || personalInfo.Id || index, // ID d'enregistrement
                    idClient: idClient, // Vrai ID client à envoyer à l'API
                    nom: personalInfo.lastName || personalInfo.LastName || '',
                    prenom: personalInfo.firstName || personalInfo.FirstName || '',
                    numeroAssure: personalInfo.numeroAssure || personalInfo.NumeroAssure || idClient.toString(),
                    police: personalInfo.numeroPolice || personalInfo.NumeroPolice || '',
                    famille: personalInfo.numBeneficiairePrincipal || personalInfo.NumBeneficiairePrincipal || '',
                    email: personalInfo.email || personalInfo.Email || '',
                    telephone: personalInfo.phoneNumber || personalInfo.PhoneNumber || '',
                    dateNaissance: personalInfo.birthdate || personalInfo.Birthdate || '',
                    statut: status.isActive || status.IsActive ? t('cardGeneration.batch.badges.active') : t('cardGeneration.batch.badges.inactive'),
                    hasActiveCard: hasActiveCard,
                    selectable: true // Tous les bénéficiaires sont sélectionnables
                };

                console.log(`Bénéficiaire mappé ${index}:`, { original: ben, adapted: result });
                return result;
            });

            console.log('Adapted Results:', adaptedResults);

            this.setState({
                searchResults: adaptedResults,
                selectedBeneficiaries: []
            });

            if (adaptedResults.length === 0) {
                this.showToast('info', t('common.info'), t('cardGeneration.batch.messages.noBeneficiaryFound'));
            } else {
                this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.beneficiariesFound', { count: adaptedResults.length }));
            }
        } catch (error) {
            console.error('Erreur recherche:', error);
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.searchError') + ': ' + (error.message || t('common.unknownError')));
        } finally {
            this.setState({ searchLoading: false });
        }
    };

    // ===== GÉNÉRATION DU BATCH =====

    validateBatch = () => {
        const { t } = this.props;
        const { selectedBeneficiaries, batchConfig } = this.state;

        if (selectedBeneficiaries.length === 0) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.noSelection'));
            return false;
        }

        if (!batchConfig.name.trim()) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.batchNameRequired'));
            return false;
        }

        if (!batchConfig.templateId) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.templateRequired'));
            return false;
        }

        if (!batchConfig.country) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.countryRequiredForBatch'));
            return false;
        }

        return true;
    };

    generateBatch = async () => {
        if (!this.validateBatch()) return;

        this.setState({ showValidationDialog: true });
    };

    confirmBatchGeneration = async () => {
        this.setState({
            showValidationDialog: false,
            activeTabIndex: 2, // Rediriger vers l'onglet de génération
            isGenerating: true,
            generationProgress: 0,
            generationStats: {
                total: this.state.selectedBeneficiaries.length,
                success: 0,
                errors: 0,
                inProgress: 0
            }
        });

        try {
            const { selectedBeneficiaries, batchConfig } = this.state;
            const { t } = this.props;

            // Simulation du progrès de génération
            const progressInterval = setInterval(() => {
                this.setState(prevState => {
                    const newProgress = Math.min(prevState.generationProgress + 10, 90);
                    return { generationProgress: newProgress };
                });
            }, 1000);

            // Extraire les vrais IDs clients
            const clientIds = selectedBeneficiaries.map(b => {
                // Priorité: idClient, puis numeroAssure (pour import fichier), puis id (pour recherche)
                const clientId = b.id;
                const parsedId = parseInt(clientId);

                if (isNaN(parsedId)) {
                    console.warn('ID invalide pour bénéficiaire:', b);
                    return null;
                }
                return parsedId;
            }).filter(id => id !== null); // Filtrer les IDs invalides

            console.log('IDs des clients à envoyer:', clientIds);
            console.log('Bénéficiaires sélectionnés:', selectedBeneficiaries);

            const batchData = {
                countryCode: batchConfig.country,
                templateId: batchConfig.templateId,
                stockId: batchConfig.stockId,
                clientIds: clientIds,
                priority: batchConfig.priority,
                requestedBy: 'admin@company.com',
                batchName: batchConfig.name,
                description: batchConfig.description,
                generatePdf: batchConfig.generatePdf
            };

            console.log('Données du batch à envoyer:', batchData);

            const result = await CarteV2Service.generateBatchCards(batchData);

            clearInterval(progressInterval);

            // Extract response data (handle both PascalCase and camelCase)
            const isSuccess = result.isSuccess !== undefined ? result.isSuccess : result.IsSuccess !== undefined ? result.IsSuccess : true;
            const batchId = result.BatchId || result.batchId;
            const batchNumber = result.BatchNumber || result.batchNumber;
            const totalCards = result.TotalCards || result.totalCards;
            const errors = result.Errors || result.errors || [];
            const message = result.Message || result.message;

            // Vérifier si la requête a échoué
            if (!isSuccess) {
                clearInterval(progressInterval);
                this.setState({
                    isGenerating: false,
                    generationProgress: 0
                });

                // Formater les erreurs
                let errorMessage = message || t('cardGeneration.batch.errors.batchGeneration');
                let errorDetails = null;

                if (errors && errors.length > 0) {
                    errorDetails = errors.map(err => `• ${err}`).join('\n');
                }

                // Afficher l'erreur
                if (errorDetails) {
                    this.showToast('error', errorMessage, errorDetails, 10000);
                } else {
                    this.showToast('error', t('common.error'), errorMessage);
                }

                return;
            }

            this.setState({
                generationProgress: 100,
                currentBatch: {
                    id: batchId,
                    batchNumber: batchNumber,
                    name: batchConfig.name,
                    status: 'InProgress'
                },
                generationStats: {
                    total: totalCards,
                    success: totalCards - errors.length,
                    errors: errors.length,
                    inProgress: 0
                }
            });

            setTimeout(() => {
                this.setState({ isGenerating: false });
                const successMsg = t('cardGeneration.batch.messages.batchSuccess', { number: batchNumber, count: totalCards });
                this.showToast('success', t('common.success'), successMsg);
                this.loadBatchHistory(); // Recharger l'historique
            }, 2000);

        } catch (error) {
            const { t } = this.props;

            this.setState({
                isGenerating: false,
                generationProgress: 0
            });

            // Parse and format error message
            let errorMessage = t('cardGeneration.batch.errors.batchGeneration');
            let errorDetails = null;

            try {
                // Vérifier si l'erreur contient une réponse JSON structurée
                let errorData = null;

                // Cas 1: L'erreur contient directement les données (depuis le service)
                if (error.response && error.response.data) {
                    errorData = error.response.data;
                }
                // Cas 2: L'erreur est dans le message au format JSON
                else if (error.message && error.message.includes('{')) {
                    const jsonStart = error.message.indexOf('{');
                    const jsonStr = error.message.substring(jsonStart);
                    errorData = JSON.parse(jsonStr);
                }

                if (errorData) {
                    // Extraire le message principal
                    errorMessage = errorData.message || errorData.Message || t('cardGeneration.batch.errors.batchCreation');

                    // Extraire et formater les erreurs
                    const errors = errorData.errors || errorData.Errors;
                    if (errors && Array.isArray(errors) && errors.length > 0) {
                        const errorsList = errors.map(err => {
                            // Vérifier si c'est une erreur de cartes existantes
                            if (err.includes('Des cartes existent déjà') || err.includes('déjà')) {
                                const clientIds = err.match(/\d+/g);
                                if (clientIds && clientIds.length > 0) {
                                    return `• ${t('cardGeneration.batch.messages.hasActiveCard')}`;
                                }
                            }
                            return `• ${err}`;
                        }).join('\n');

                        errorDetails = errorsList;
                    }
                }
            } catch (parseError) {
                // Si le parsing échoue, utiliser le message d'erreur original
                console.error('Erreur lors du parsing de l\'erreur:', parseError);
                errorMessage = error.message || t('cardGeneration.batch.errors.batchGeneration');
            }

            // Afficher l'erreur formatée
            if (errorDetails) {
                this.showToast('error', errorMessage, errorDetails, 10000);
            } else {
                this.showToast('error', t('common.error'), errorMessage);
            }
        }
    };

    // ===== ACTIONS SUR LES BATCHES =====

    downloadBatchPdf = async (batch) => {
        const { t } = this.props;
        try {
            // Afficher un loader pendant le téléchargement
            this.setState({
                isPdfDownloading: true,
                downloadProgress: 0
            });

            // Simuler la progression du téléchargement
            const progressInterval = setInterval(() => {
                this.setState(prevState => {
                    const newProgress = Math.min(prevState.downloadProgress + 15, 90);
                    return { downloadProgress: newProgress };
                });
            }, 500);

            const pdfBlob = await CarteV2Service.batchPrintCardsPdf({
                batchId: batch.id,
                format: 'A4',
                cardsPerPage: 10
            });

            clearInterval(progressInterval);
            this.setState({ downloadProgress: 100 });

            CarteV2Service.downloadFile(pdfBlob, `batch_${batch.name}_${Date.now()}.pdf`);

            setTimeout(() => {
                this.setState({
                    isPdfDownloading: false,
                    downloadProgress: 0
                });
                this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.downloadPdf'));
            }, 500);
        } catch (error) {
            this.setState({
                isPdfDownloading: false,
                downloadProgress: 0
            });
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.downloadError'));
        }
    };

    retryFailedCards = async (batch) => {
        const { t } = this.props;
        try {
            // Relancer les cartes en échec
            this.showToast('info', t('common.info'), t('cardGeneration.batch.messages.retryFailed'));
        } catch (error) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.retryError'));
        }
    };

    // Générer une image placeholder pour la prévisualisation
    generatePlaceholderImage = () => {
        return new Promise((resolve) => {
            const canvas = document.createElement('canvas');
            canvas.width = 300;
            canvas.height = 400;
            const ctx = canvas.getContext('2d');

            // Fond gris
            ctx.fillStyle = '#E0E0E0';
            ctx.fillRect(0, 0, 300, 400);

            // Icône silhouette
            ctx.fillStyle = '#BDBDBD';
            ctx.beginPath();
            ctx.arc(150, 120, 50, 0, Math.PI * 2); // Tête
            ctx.fill();
            ctx.beginPath();
            ctx.ellipse(150, 250, 80, 100, 0, 0, Math.PI * 2); // Corps
            ctx.fill();

            // Texte
            ctx.fillStyle = '#757575';
            ctx.font = '16px Arial';
            ctx.textAlign = 'center';
            ctx.fillText('PHOTO', 150, 340);
            ctx.fillText('PREVIEW', 150, 360);

            // Convertir en base64
            const base64 = canvas.toDataURL('image/png').split(',')[1];
            resolve(base64);
        });
    };

    // Prévisualisation du template
    handleTemplatePreview = async () => {
        const { t } = this.props;
        const { batchConfig, selectedBeneficiaries } = this.state;

        if (!batchConfig.templateId) {
            this.showToast('warn', t('common.toast.warning'), t('cardGeneration.batch.messages.templateRequired'));
            return;
        }

        this.setState({ previewLoading: true, showPreviewDialog: true });

        try {
            // Utiliser le premier bénéficiaire sélectionné ou des données exemple
            let previewData = {
                firstName: 'LAST FIRST NAME',
                lastName: 'EXAMPLE',
                cardNumber: '123456',
                birthDate: new Date().toISOString(),
                policyNumber: 'POL001',
                photoBase64: await this.generatePlaceholderImage(),
                beneficiaryId: 0,
                serialNumber: 'PREVIEW',
                nationality: batchConfig.country === 'LB' ? 'Liberian' :
                            batchConfig.country === 'CI' ? 'Ivoirienne' :
                            batchConfig.country === 'CM' ? 'Camerounaise' : 'N/A',
                customFields: {
                    country: batchConfig.country,
                    priority: batchConfig.priority,
                    notes: 'Template preview'
                }
            };

            // Si on a des bénéficiaires sélectionnés, utiliser le premier
            if (selectedBeneficiaries.length > 0) {
                const firstBen = selectedBeneficiaries[0];

                // Gérer la date de naissance - convertir en ISO string si nécessaire
                let birthDateISO = new Date().toISOString();
                const birthDateValue = firstBen.dateNaissance || firstBen.birthDate;
                if (birthDateValue) {
                    try {
                        const dateObj = new Date(birthDateValue);
                        if (!isNaN(dateObj.getTime())) {
                            birthDateISO = dateObj.toISOString();
                        }
                    } catch (error) {
                        console.warn('Erreur parsing date pour preview:', error);
                    }
                }

                previewData = {
                    firstName: firstBen.prenom || firstBen.firstName || 'PRENOM',
                    lastName: firstBen.nom || firstBen.lastName || 'NOM',
                    cardNumber: firstBen.numeroAssure || firstBen.id || 'PREVIEW',
                    birthDate: birthDateISO,
                    policyNumber: firstBen.police || firstBen.policeNumber || 'POL001',
                    photoBase64: await this.generatePlaceholderImage(),
                    beneficiaryId: firstBen.id || 0,
                    serialNumber: '',
                    nationality: batchConfig.country === 'LB' ? 'Liberian' :                                batchConfig.country === 'LB' ? 'Liberian' :
                                batchConfig.country === 'CI' ? 'Ivoirienne' :
                                batchConfig.country === 'CM' ? 'Camerounaise' : 'N/A',
                    customFields: {
                        country: batchConfig.country,
                        priority: batchConfig.priority,
                        notes: batchConfig.description || ''
                    }
                };
            }

            const url = await CarteV2Service.getTemplatePreviewUrl(batchConfig.templateId, previewData);
            this.setState({
                previewUrl: url,
                previewLoading: false
            });
            this.showToast('success', t('cardGeneration.batch.labels.preview'), t('cardGeneration.batch.messages.previewGenerated'));
        } catch (error) {
            console.error('Erreur prévisualisation:', error);
            this.setState({ previewLoading: false });
            const errorMsg = error.response?.data?.[0] || error.message || t('cardGeneration.batch.errors.unknown');
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.generatePreview') + ': ' + errorMsg);
        }
    };

    // ===== CRÉATION PAR LOT =====

    downloadCreationTemplate = () => {
        const { t } = this.props;
        // Create CSV template for batch creation
        // Note: Le backend générera automatiquement les numéros assurés
        //
        // UTILISATION:
        // - Regrouper les membres d'une même famille avec le même temporaryFamilyId
        // - Un membre de chaque famille doit avoir relationshipWithMainInsured = 'PRINCIPAL'
        // - Les autres membres peuvent avoir 'SPOUSE', 'DEPENDENT', 'CHILD', etc.
        // - Le backend créera les numéros assurés et établira les liens automatiquement
        const csvContent = [
            ['firstName', 'lastName', 'birthdate', 'numeroPolice', 'sexe', 'email', 'phoneNumber', 'subscriberName', 'mainInsuredName', 'temporaryFamilyId', 'relationshipWithMainInsured'],
            ['Paul', 'MARTIN', '1980-03-10', 'POL456', 'M', 'paul.martin@example.com', '+225987654321', 'COMPANY XYZ', 'Paul MARTIN', 'FAM001', 'PRINCIPAL'],
            ['Sophie', 'MARTIN', '1982-05-15', 'POL456', 'F', 'sophie.martin@example.com', '+225987654321', 'COMPANY XYZ', 'Paul MARTIN', 'FAM001', 'SPOUSE'],
            ['Jean', 'BARRY', '1985-05-15', 'POL789', 'M', 'jean.barry@example.com', '+237123456789', 'COMPANY ABC', 'Jean BARRY', 'FAM002', 'PRINCIPAL'],
            ['Karl', 'BARRY', '2010-06-15', 'POL789', 'M', 'karl.barry@example.com', '+237123456790', 'COMPANY ABC', 'Jean BARRY', 'FAM002', 'DEPENDENT'],
            ['Anna', 'BARRY', '2012-07-20', 'POL789', 'F', 'anna.barry@example.com', '+237123456791', 'COMPANY ABC', 'Jean BARRY', 'FAM002', 'DEPENDENT']
        ];

        const csv = csvContent.map(row => row.join(',')).join('\n');
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);

        link.setAttribute('href', url);
        link.setAttribute('download', `template_creation_batch_${Date.now()}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.creationTemplateDownloaded'));
    };

    parseCreationFile = async (file) => {
        const { t } = this.props;
        try {
            const reader = new FileReader();

            reader.onload = (e) => {
                try {
                    const data = new Uint8Array(e.target.result);
                    const workbook = XLSX.read(data, { type: 'array', cellDates: true });

                    const firstSheetName = workbook.SheetNames[0];
                    const worksheet = workbook.Sheets[firstSheetName];

                    const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false, dateNF: 'yyyy-mm-dd' });

                    if (jsonData.length === 0) {
                        this.showToast('warn', t('common.warning'), t('cardGeneration.batch.warnings.fileEmpty'));
                        return;
                    }

                    // Process data for batch creation
                    const processedData = jsonData.map((row, index) => {
                        const errors = [];

                        // Extract fields
                        const firstName = row.firstName || row.FirstName || row.prenom || row.Prenom || '';
                        const lastName = row.lastName || row.LastName || row.nom || row.Nom || '';
                        let birthdate = row.birthdate || row.Birthdate || row.birthDate || row.dateNaissance || '';
                        const numeroPolice = row.numeroPolice || row.NumeroPolice || row.police || row.Police || '';
                        const sexe = row.sexe || row.Sexe || row.gender || row.Gender || '';
                        const email = row.email || row.Email || '';
                        const phoneNumber = row.phoneNumber || row.PhoneNumber || row.telephone || row.Telephone || '';
                        const subscriberName = row.subscriberName || row.SubscriberName || row.nomSouscripteur || row.NomSouscripteur || '';
                        const mainInsuredName = row.mainInsuredName || row.MainInsuredName || row.assurePrincipal || row.AssurePrincipal || '';
                        const temporaryFamilyId = row.temporaryFamilyId || row.TemporaryFamilyId || row.familleTemporaire || row.FamilleTemporaire || '';
                        const relationshipWithMainInsured = row.relationshipWithMainInsured || row.RelationshipWithMainInsured || row.lienParente || row.LienParente || '';

                        // Convert date if Excel serial number
                        if (typeof birthdate === 'number') {
                            birthdate = this.excelDateToJSDate(birthdate);
                        } else if (birthdate) {
                            birthdate = birthdate.toString().trim();
                        }

                        // Required field validation
                        if (!firstName || firstName.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.firstNameMissing'));
                        }
                        if (!lastName || lastName.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.lastNameMissing'));
                        }
                        if (!birthdate || birthdate.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.birthDateMissing'));
                        }
                        if (!numeroPolice || numeroPolice.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.policeNumberMissing'));
                        }
                        if (!sexe || sexe.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.genderMissing'));
                        }
                        if (!subscriberName || subscriberName.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.subscriberNameMissing'));
                        }
                        if (!mainInsuredName || mainInsuredName.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.mainInsuredNameMissing'));
                        }
                        if (!temporaryFamilyId || temporaryFamilyId.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.temporaryFamilyIdMissing'));
                        }
                        if (!relationshipWithMainInsured || relationshipWithMainInsured.toString().trim() === '') {
                            errors.push(t('cardGeneration.batch.validation.relationshipMissing'));
                        }

                        return {
                            id: index + 1,
                            firstName: firstName.toString().trim(),
                            lastName: lastName.toString().trim(),
                            birthdate: birthdate || '',
                            numeroPolice: numeroPolice.toString().trim(),
                            sexe: sexe.toString().trim(),
                            email: email ? email.toString().trim() : '',
                            phoneNumber: phoneNumber ? phoneNumber.toString().trim() : '',
                            subscriberName: subscriberName ? subscriberName.toString().trim() : '',
                            mainInsuredName: mainInsuredName ? mainInsuredName.toString().trim() : '',
                            temporaryFamilyId: temporaryFamilyId ? temporaryFamilyId.toString().trim() : '', // ID pour grouper les membres de la famille
                            relationshipWithMainInsured: relationshipWithMainInsured ? relationshipWithMainInsured.toString().trim() : '',
                            clientPhoto: null, // Will be populated with base64
                            status: errors.length === 0 ? 'valid' : 'error',
                            errors: errors
                        };
                    });

                    const validData = processedData.filter(b => b.status === 'valid');
                    const invalidData = processedData.filter(b => b.status === 'error');

                    this.setState({
                        creationData: validData,
                        creationValidationErrors: invalidData
                    });

                    this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.importedForCreation', { count: validData.length }));

                    if (invalidData.length > 0) {
                        this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.linesWithErrors', { count: invalidData.length }));
                    }
                } catch (parseError) {
                    console.error('Erreur parsing:', parseError);
                    this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.fileParsing') + ': ' + parseError.message);
                }
            };

            reader.onerror = () => {
                this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.fileReading'));
            };

            reader.readAsArrayBuffer(file);

        } catch (error) {
            console.error('Erreur parseCreationFile:', error);
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.fileProcessing') + ': ' + error.message);
        }
    };

    handleCreationFileUpload = (event) => {
        const { t } = this.props;
        const file = event.files[0];
        if (!file) return;

        const allowedTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
        ];

        if (!allowedTypes.includes(file.type)) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.warnings.fileTypeNotSupported'));
            return;
        }

        this.parseCreationFile(file);
    };

    handleBatchPhotoUpload = async (event, beneficiaryId) => {
        const { t } = this.props;
        const file = event.files[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.warnings.photoFormatNotSupported'));
            return;
        }

        // Validate file size (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.warnings.photoSizeExceeded'));
            return;
        }

        // Convert to base64
        try {
            const base64 = await this.imageToBase64(file);

            // Update the creationData with the photo
            this.setState(prevState => ({
                creationData: prevState.creationData.map(ben =>
                    ben.id === beneficiaryId ? { ...ben, clientPhoto: base64 } : ben
                )
            }));

            this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.photoAdded'));
        } catch (error) {
            this.showToast('error', t('common.error'), t('cardGeneration.batch.errors.photoProcessing'));
        }
    };

    imageToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => {
                // Extract base64 string without data URL prefix
                const base64String = reader.result.split(',')[1];
                resolve(base64String);
            };
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    };

    // Bulk photo upload functions
    normalizeString = (str) => {
        return str
            .toString()
            .toLowerCase()
            .trim()
            .replace(/\s+/g, '_')
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, ''); // Remove diacritics
    };

    matchPhotosByNaming = (photos, beneficiaries) => {
        const matched = [];
        const orphanPhotos = [];
        const beneficiariesWithPhotos = new Set();

        photos.forEach(photo => {
            const fileName = photo.name.replace(/\.(jpg|jpeg|png)$/i, '');
            const normalizedFileName = this.normalizeString(fileName);
            let matchFound = false;

            // Try matching by firstName_lastName
            const byName = beneficiaries.find(ben => {
                const pattern1 = this.normalizeString(`${ben.firstName}_${ben.lastName}`);
                const pattern2 = this.normalizeString(`${ben.lastName}_${ben.firstName}`);
                return pattern1 === normalizedFileName || pattern2 === normalizedFileName;
            });

            if (byName) {
                matched.push({
                    beneficiary: byName,
                    photo: photo,
                    matchType: 'name'
                });
                beneficiariesWithPhotos.add(byName.id);
                matchFound = true;
            }

            if (!matchFound) {
                orphanPhotos.push(photo);
            }
        });

        const unmatched = beneficiaries.filter(ben => !beneficiariesWithPhotos.has(ben.id));

        return { matched, unmatched, orphanPhotos };
    };

    handleBulkPhotoZipUpload = async (event) => {
        const { t } = this.props;
        const { creationData } = this.state;
        const file = event.files[0];

        if (!file) return;

        // Validate file type
        if (!file.name.toLowerCase().endsWith('.zip')) {
            this.showToast('error', t('common.error'), 'Only ZIP files are supported');
            return;
        }

        // Validate file size (50MB max)
        if (file.size > 50 * 1024 * 1024) {
            this.showToast('error', t('common.error'), 'ZIP file must be less than 50MB');
            return;
        }

        if (creationData.length === 0) {
            this.showToast('warn', t('common.warning'), 'Please import beneficiaries first');
            return;
        }

        this.setState({ bulkPhotoProcessing: true });

        try {
            const zip = await JSZip.loadAsync(file);
            const photoFiles = [];

            // Extract all image files from ZIP (including subdirectories)
            for (const [filepath, zipEntry] of Object.entries(zip.files)) {
                if (zipEntry.dir) continue;

                // Get just the filename without the path
                const filename = filepath.split('/').pop();

                // Skip hidden files and system files
                if (filename.startsWith('.') || filename.startsWith('__')) continue;

                const ext = filename.split('.').pop().toLowerCase();
                if (['jpg', 'jpeg', 'png'].includes(ext)) {
                    const blob = await zipEntry.async('blob');
                    // Use only the filename (not the full path) for matching
                    const photoFile = new File([blob], filename, { type: `image/${ext === 'jpg' ? 'jpeg' : ext}` });
                    photoFiles.push(photoFile);
                }
            }

            if (photoFiles.length === 0) {
                this.showToast('warn', 'No Images Found', 'No valid image files found in ZIP');
                this.setState({ bulkPhotoProcessing: false });
                return;
            }

            // Match photos with beneficiaries
            const matchingResults = this.matchPhotosByNaming(photoFiles, creationData);

            // Convert matched photos to base64
            for (const match of matchingResults.matched) {
                const base64 = await this.imageToBase64(match.photo);
                match.base64 = base64;
            }

            // Auto-apply if 100% match
            if (matchingResults.orphanPhotos.length === 0 && matchingResults.unmatched.length === 0) {
                // Perfect match - apply automatically
                this.setState(prevState => ({
                    creationData: prevState.creationData.map(ben => {
                        const match = matchingResults.matched.find(m => m.beneficiary.id === ben.id);
                        if (match) {
                            return { ...ben, clientPhoto: match.base64 };
                        }
                        return ben;
                    }),
                    bulkPhotoProcessing: false
                }), () => {
                    // Show success message and scroll to beneficiary list
                    this.showToast('success', t('common.success'),
                        `✅ ${t('cardGeneration.batch.messages.perfectMatch') || 'Perfect Match!'} (${matchingResults.matched.length}/${matchingResults.matched.length})`);

                    // Scroll to beneficiary list for visual verification
                    setTimeout(() => {
                        const beneficiaryTable = document.querySelector('.p-datatable');
                        if (beneficiaryTable) {
                            beneficiaryTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 500);
                });
            } else {
                // Show dialog for review
                this.setState({
                    photoMatchingResults: matchingResults,
                    showPhotoMatchingDialog: true,
                    bulkPhotoProcessing: false
                });
            }

        } catch (error) {
            this.showToast('error', 'Error', 'Failed to process ZIP file');
            this.setState({ bulkPhotoProcessing: false });
        }
    };

    handleBulkPhotoMultipleUpload = async (event) => {
        const { t } = this.props;
        const { creationData } = this.state;
        const files = event.files;

        if (!files || files.length === 0) return;

        if (creationData.length === 0) {
            this.showToast('warn', t('common.warning'), 'Please import beneficiaries first');
            return;
        }

        this.setState({ bulkPhotoProcessing: true });

        try {
            // Filter valid image files
            const photoFiles = Array.from(files).filter(file => {
                const ext = file.name.split('.').pop().toLowerCase();
                return ['jpg', 'jpeg', 'png'].includes(ext) && file.size <= 5 * 1024 * 1024;
            });

            if (photoFiles.length === 0) {
                this.showToast('warn', 'No Images Found', 'No valid image files selected');
                this.setState({ bulkPhotoProcessing: false });
                return;
            }

            // Match photos with beneficiaries
            const matchingResults = this.matchPhotosByNaming(photoFiles, creationData);

            // Convert matched photos to base64
            for (const match of matchingResults.matched) {
                const base64 = await this.imageToBase64(match.photo);
                match.base64 = base64;
            }

            // Auto-apply if 100% match
            if (matchingResults.orphanPhotos.length === 0 && matchingResults.unmatched.length === 0) {
                // Perfect match - apply automatically
                this.setState(prevState => ({
                    creationData: prevState.creationData.map(ben => {
                        const match = matchingResults.matched.find(m => m.beneficiary.id === ben.id);
                        if (match) {
                            return { ...ben, clientPhoto: match.base64 };
                        }
                        return ben;
                    }),
                    bulkPhotoProcessing: false
                }), () => {
                    // Show success message and scroll to beneficiary list
                    this.showToast('success', t('common.success'),
                        `✅ ${t('cardGeneration.batch.messages.perfectMatch') || 'Perfect Match!'} (${matchingResults.matched.length}/${matchingResults.matched.length})`);

                    // Scroll to beneficiary list for visual verification
                    setTimeout(() => {
                        const beneficiaryTable = document.querySelector('.p-datatable');
                        if (beneficiaryTable) {
                            beneficiaryTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                        }
                    }, 500);
                });
            } else {
                // Show dialog for review
                this.setState({
                    photoMatchingResults: matchingResults,
                    showPhotoMatchingDialog: true,
                    bulkPhotoProcessing: false
                });
            }

        } catch (error) {
            this.showToast('error', 'Error', 'Failed to process photos');
            this.setState({ bulkPhotoProcessing: false });
        }
    };

    applyPhotoMatching = () => {
        const { photoMatchingResults } = this.state;
        const { t } = this.props;

        // Update creationData with matched photos
        this.setState(prevState => ({
            creationData: prevState.creationData.map(ben => {
                const match = photoMatchingResults.matched.find(m => m.beneficiary.id === ben.id);
                if (match) {
                    return { ...ben, clientPhoto: match.base64 };
                }
                return ben;
            }),
            showPhotoMatchingDialog: false
        }), () => {
            // Callback after state update - close photo panel if all photos are assigned
            const totalBeneficiaries = this.state.creationData.length;
            const assignedPhotos = this.state.creationData.filter(b => b.clientPhoto).length;

            if (assignedPhotos === totalBeneficiaries) {
                // All photos assigned - show success message and scroll to beneficiary list
                this.showToast('success', t('common.success'),
                    `${t('cardGeneration.batch.messages.allPhotosAssigned')} (${assignedPhotos}/${totalBeneficiaries})`);

                // Scroll to beneficiary list for visual verification
                setTimeout(() => {
                    const beneficiaryTable = document.querySelector('.p-datatable');
                    if (beneficiaryTable) {
                        beneficiaryTable.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }
                }, 300);
            } else {
                this.showToast('success', t('common.success'),
                    `${photoMatchingResults.matched.length} ${t('cardGeneration.batch.messages.photosAssigned')} (${assignedPhotos}/${totalBeneficiaries})`);
            }
        });
    };

    assignOrphanPhoto = async (orphanPhoto, beneficiaryId) => {
        const { t } = this.props;

        try {
            const base64 = await this.imageToBase64(orphanPhoto);

            this.setState(prevState => ({
                creationData: prevState.creationData.map(ben =>
                    ben.id === beneficiaryId ? { ...ben, clientPhoto: base64 } : ben
                ),
                photoMatchingResults: {
                    ...prevState.photoMatchingResults,
                    orphanPhotos: prevState.photoMatchingResults.orphanPhotos.filter(p => p !== orphanPhoto),
                    matched: [
                        ...prevState.photoMatchingResults.matched,
                        {
                            beneficiary: prevState.creationData.find(b => b.id === beneficiaryId),
                            photo: orphanPhoto,
                            matchType: 'manual',
                            base64: base64
                        }
                    ],
                    unmatched: prevState.photoMatchingResults.unmatched.filter(b => b.id !== beneficiaryId)
                }
            }));

            this.showToast('success', t('common.success'), 'Photo assigned successfully');
        } catch (error) {
            this.showToast('error', t('common.error'), 'Error assigning photo');
        }
    };

    createBatchBeneficiaries = async () => {
        const { t } = this.props;
        const { creationData, batchConfig } = this.state;

        if (creationData.length === 0) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.noBeneficiaryToCreate'));
            return;
        }

        if (!batchConfig.country) {
            this.showToast('warn', t('common.warning'), t('cardGeneration.batch.messages.countryRequired'));
            return;
        }

        try {
            this.setState({ isGenerating: true, generationProgress: 10 });


            // Map data to enrollment batch structure
            const enrollments = creationData.map(ben => {
                // Convert birthdate to ISO 8601 format with time
                let birthDateISO;
                try {
                    // Parse the date string (format: YYYY-MM-DD)
                    const dateParts = ben.birthdate.split('-');
                    if (dateParts.length === 3) {
                        // Create date object and convert to ISO string
                        const dateObj = new Date(dateParts[0], dateParts[1] - 1, dateParts[2], 0, 0, 0);
                        birthDateISO = dateObj.toISOString();
                    } else {
                        // Try direct parsing
                        birthDateISO = new Date(ben.birthdate).toISOString();
                    }
                } catch (error) {
                    console.error('Erreur conversion date:', error);
                    // Fallback: use current date
                    birthDateISO = new Date().toISOString();
                }

                return {
                    client: {
                        firstName: ben.firstName,
                        lastName: ben.lastName,
                        birthDate: birthDateISO,
                        policyNumber: ben.numeroPolice,
                        gender: ben.sexe,
                        email: ben.email || "",
                        phoneNumber: ben.phoneNumber || "",
                        souscripteur: ben.subscriberName || "",
                        assurePrincipalName: ben.mainInsuredName || "",
                        temporaryFamilyId: ben.temporaryFamilyId || null,  // ID pour grouper les membres d'une famille
                        lienParente: ben.relationshipWithMainInsured || "",  // Relation avec l'assuré principal
                        photoPath: ben.clientPhoto || ""
                    },
                    documents: [],
                    restrictions: [],
                    enrollmentType: "New",
                    countryCode: batchConfig.country,
                    priority: batchConfig.priority || "Normal",
                    notes: `${t('cardGeneration.batch.messages.batchCreated', { count: 1 })} - ${batchConfig.name || 'Batch'}`
                };
            });

            const batchData = {
                batchName: batchConfig.name || `${t('cardGeneration.batch.labels.batchName')} ${Date.now()}`,
                countryCode: batchConfig.country,
                enrollments: enrollments,
                createdBy: 'admin@company.com'
            };

            console.log('Données de création batch:', batchData);

            const result = await CarteV2Service.createBatchEnrollments(batchData);
            console.log('Résultat création batch:', result);

            // Vérifier si la réponse contient un statut d'échec
            const isSuccess = result.isSuccess !== undefined ? result.isSuccess : result.IsSuccess !== undefined ? result.IsSuccess : response.ok;
            const message = result.message || result.Message;
            const errors = result.errors || result.Errors || [];

            if (!isSuccess || !response.ok) {
                // Formater les erreurs du backend
                let errorMessage = message || t('cardGeneration.batch.errors.batchCreation');
                let errorDetails = null;

                if (errors && errors.length > 0) {
                    errorDetails = errors.map(err => `• ${err}`).join('\n');
                }

                // Réinitialiser l'état
                this.setState({
                    isGenerating: false,
                    generationProgress: 0
                });

                // Afficher l'erreur formatée
                if (errorDetails) {
                    this.showToast('error', errorMessage, errorDetails, 10000);
                } else {
                    this.showToast('error', t('common.error'), errorMessage);
                }

                return;
            }

            // Succès - Mettre à jour le progrès
            this.setState({
                generationProgress: 50
            });

            // Afficher le message de succès
            const successMessage = message || t('cardGeneration.batch.messages.batchCreated', { count: result.enrollmentCount || enrollments.length });
            this.showToast('success', t('common.success'), successMessage);

            // Nettoyer les données et rester sur l'onglet d'import
            this.setState({
                creationData: [],
                creationValidationErrors: [],
                generationProgress: 100,
                isGenerating: false
            });

        } catch (error) {
            console.error('Erreur création batch:', error);

            // Parse and format error message
            let errorMessage = t('cardGeneration.batch.errors.batchCreation');
            let errorDetails = null;

            try {
                // Vérifier si l'erreur contient une réponse JSON structurée
                let errorData = null;

                // Cas 1: L'erreur contient directement les données
                if (error.response && error.response.data) {
                    errorData = error.response.data;
                }
                // Cas 2: L'erreur est dans le message au format JSON
                else if (error.message && error.message.includes('{')) {
                    const jsonStart = error.message.indexOf('{');
                    const jsonStr = error.message.substring(jsonStart);
                    errorData = JSON.parse(jsonStr);
                }

                if (errorData) {
                    // Extraire le message principal
                    errorMessage = errorData.message || errorData.Message || t('cardGeneration.batch.errors.batchCreation');

                    // Extraire et formater les erreurs
                    const errors = errorData.errors || errorData.Errors;
                    if (errors && Array.isArray(errors) && errors.length > 0) {
                        errorDetails = errors.map(err => `• ${err}`).join('\n');
                    }
                }
            } catch (parseError) {
                // Si le parsing échoue, utiliser le message d'erreur original
                console.error('Erreur lors du parsing de l\'erreur:', parseError);
                errorMessage = error.message || t('cardGeneration.batch.errors.batchCreation');
            }

            // Réinitialiser l'état
            this.setState({
                isGenerating: false,
                generationProgress: 0
            });

            // Afficher l'erreur formatée
            if (errorDetails) {
                this.showToast('error', errorMessage, errorDetails, 10000);
            } else {
                this.showToast('error', t('common.error'), errorMessage);
            }
        }
    };

    // ===== RENDU DES COMPOSANTS =====

    renderImportTab = () => {
        const { t } = this.props;
        const { importMethod, importedBeneficiaries, validationErrors } = this.state;

        return (
            <div className="import-tab">
                <Card title={t('cardGeneration.batch.panels.fileImport')}>
                    <div className="import-methods mb-4">
                        <div className="flex gap-3">
                            <Button
                                label={t('cardGeneration.batch.importMethods.creation')}
                                icon="pi pi-user-plus"
                                className={importMethod === 'creation' ? 'p-button-outlined' : 'p-button-text'}
                                onClick={() => this.setState({ importMethod: 'creation' })}
                            />
                            <Button
                                label={t('cardGeneration.batch.importMethods.search')}
                                icon="pi pi-search"
                                className={importMethod === 'search' ? 'p-button-outlined' : 'p-button-text'}
                                onClick={() => this.setState({ importMethod: 'search' })}
                            />
                        </div>
                    </div>

                    {importMethod === 'search' && this.renderCriteriaSearch()}
                    {importMethod === 'creation' && this.renderBatchCreation()}
                </Card>
            </div>
        );
    };

    renderFileImport = () => {
        const { t } = this.props;
        const { importedBeneficiaries, validationErrors } = this.state;

        return (
            <div className="file-import">
                <Panel header={t('cardGeneration.batch.panels.fileImport')} className="mb-3">
                    <div className="mb-3">
                        <Button
                            label={t('cardGeneration.batch.buttons.downloadTemplate')}
                            icon="pi pi-download"
                            className="p-button-help mb-3"
                            onClick={this.downloadTemplate}
                        />
                        <small className="d-block text-muted mb-2">
                            {t('cardGeneration.batch.messages.importHint')}
                        </small>
                    </div>

                    <FileUpload
                        name="batchFile"
                        accept=".xlsx,.xls,.csv"
                        maxFileSize={5000000}
                        customUpload
                        uploadHandler={this.handleFileUpload}
                        auto
                        chooseLabel={t('cardGeneration.batch.buttons.chooseFile')}
                        uploadLabel={t('cardGeneration.batch.buttons.importFile')}
                        cancelLabel={t('cardGeneration.batch.buttons.cancel')}
                    />

                    {/* Show imported data */}
                    {importedBeneficiaries.length > 0 && (
                        <div className="mt-4">
                            <div className="flex justify-content-between align-items-center mb-3">
                                <h6>{t('cardGeneration.batch.panels.importedData', { count: importedBeneficiaries.length })}</h6>
                                <div>
                                    <Button
                                        label={t('cardGeneration.batch.buttons.selectAll')}
                                        icon="pi pi-check-square"
                                        className="p-button-outlined p-button-sm mr-2"
                                        onClick={() => this.setState({ selectedBeneficiaries: importedBeneficiaries })}
                                    />
                                    <Button
                                        label={t('cardGeneration.batch.buttons.deselectAll')}
                                        icon="pi pi-minus-circle"
                                        className="p-button-outlined p-button-sm mr-2"
                                        onClick={() => this.setState({ selectedBeneficiaries: [] })}
                                    />
                                    <Badge
                                        value={`${this.state.selectedBeneficiaries.length} ${t('cardGeneration.batch.labels.selected')}`}
                                        severity="info"
                                    />
                                </div>
                            </div>
                            <DataTable
                                value={importedBeneficiaries}
                                selection={this.state.selectedBeneficiaries}
                                onSelectionChange={(e) => this.setState({ selectedBeneficiaries: e.value })}
                                dataKey="id"
                                paginator
                                rows={5}
                                rowsPerPageOptions={[5, 10, 20]}
                            >
                                <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
                                <Column field="numeroAssure" header={t('cardGeneration.batch.table.columns.insuredNumber')} />
                                <Column field="nom" header={t('cardGeneration.batch.table.columns.lastName')} />
                                <Column field="prenom" header={t('cardGeneration.batch.table.columns.firstName')} />
                                <Column field="police" header={t('cardGeneration.batch.table.columns.police')} />
                                <Column field="famille" header={t('cardGeneration.batch.table.columns.family')} />
                                <Column field="dateNaissance" header={t('cardGeneration.batch.table.columns.birthDate')} />
                            </DataTable>

                            <div className="mt-3 text-right">
                                <Button
                                    label={t('cardGeneration.batch.buttons.next') + ` (${this.state.selectedBeneficiaries.length} ${t('cardGeneration.batch.labels.selected')})`}
                                    icon="pi pi-arrow-right"
                                    iconPos="right"
                                    className="p-button-success"
                                    disabled={this.state.selectedBeneficiaries.length === 0}
                                    onClick={() => this.setState({ activeTabIndex: 1 })}
                                />
                            </div>
                        </div>
                    )}

                    {validationErrors.length > 0 && (
                        <div className="mt-3">
                            <h6 className="text-danger">{t('cardGeneration.batch.panels.errors', { count: validationErrors.length })}</h6>
                            <DataTable value={validationErrors}>
                                <Column field="nom" header={t('cardGeneration.batch.table.columns.lastName')} />
                                <Column field="prenom" header={t('cardGeneration.batch.table.columns.firstName')} />
                                <Column
                                    field="errors"
                                    header={t('cardGeneration.batch.table.columns.errors')}
                                    body={(rowData) => (
                                        <ul className="mb-0">
                                            {rowData.errors?.map((err, idx) => (
                                                <li key={idx} className="text-danger">{err}</li>
                                            ))}
                                        </ul>
                                    )}
                                />
                            </DataTable>
                        </div>
                    )}
                </Panel>
            </div>
        );
    };

    renderBatchCreation = () => {
        const { t } = this.props;
        const { creationData, creationValidationErrors } = this.state;

        return (
            <div className="batch-creation">
                <Panel header={t('cardGeneration.batch.panels.batchCreation')} className="mb-3">
                    <div className="mb-3">
                        <Button
                            label={t('cardGeneration.batch.buttons.downloadCreationTemplate')}
                            icon="pi pi-download"
                            className="p-button-help mb-3"
                            onClick={this.downloadCreationTemplate}
                        />
                        <small className="d-block text-muted mb-2">
                            {t('cardGeneration.batch.messages.creationHint')}
                        </small>
                    </div>

                    <FileUpload
                        name="creationFile"
                        accept=".xlsx,.xls,.csv"
                        maxFileSize={5000000}
                        customUpload
                        uploadHandler={this.handleCreationFileUpload}
                        auto
                        chooseLabel={t('cardGeneration.batch.buttons.chooseFile')}
                        uploadLabel={t('cardGeneration.batch.buttons.importFile')}
                        cancelLabel={t('cardGeneration.batch.buttons.cancel')}
                    />

                    {creationData.length > 0 && (
                        <div className="mt-4">
                            {/* Bulk Photo Upload Section - Enhanced */}
                            <Panel
                                header={
                                    <div className="flex align-items-center justify-content-between w-full">
                                        <div className="flex align-items-center gap-2">
                                            <i className="pi pi-images" style={{ fontSize: '1.2rem' }}></i>
                                            <span style={{ fontWeight: 600 }}>
                                                {t('cardGeneration.batch.panels.uploadPhotos')}
                                            </span>
                                        </div>
                                        <Badge
                                            value={`${creationData.filter(b => b.clientPhoto).length}/${creationData.length}`}
                                            severity={
                                                creationData.filter(b => b.clientPhoto).length === creationData.length
                                                    ? 'success'
                                                    : creationData.filter(b => b.clientPhoto).length > 0
                                                        ? 'warning'
                                                        : 'info'
                                            }
                                            style={{ fontSize: '0.9rem', marginLeft: '10px' }}
                                        />
                                    </div>
                                }
                                className="mb-3"
                                toggleable
                                collapsed={creationData.filter(b => b.clientPhoto).length === creationData.length}
                            >
                                <div className="grid">
                                    <div className="col-12 md:col-6">
                                        <FileUpload
                                            name="bulkPhotosZip"
                                            accept=".zip"
                                            maxFileSize={50000000}
                                            customUpload
                                            uploadHandler={this.handleBulkPhotoZipUpload}
                                            auto
                                            chooseLabel="📦 ZIP File (50MB max)"
                                            disabled={this.state.bulkPhotoProcessing}
                                        />
                                        <small className="text-muted d-block mt-1">Upload all photos in a ZIP file</small>
                                    </div>
                                    <div className="col-12 md:col-6">
                                        <FileUpload
                                            name="bulkPhotosMultiple"
                                            accept="image/jpeg,image/jpg,image/png"
                                            maxFileSize={5000000}
                                            multiple
                                            customUpload
                                            uploadHandler={this.handleBulkPhotoMultipleUpload}
                                            auto
                                            chooseLabel="🖼️ Multiple Images"
                                            disabled={this.state.bulkPhotoProcessing}
                                        />
                                        <small className="text-muted d-block mt-1">Select multiple image files</small>
                                    </div>
                                </div>

                                <div className="mt-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', fontSize: '0.85rem' }}>
                                    <strong>📝 Naming:</strong> <code>123456.jpg</code> or <code>Jean_DUPONT.jpg</code>
                                </div>

                                {this.state.bulkPhotoProcessing && (
                                    <div className="mt-3 text-center">
                                        <ProgressSpinner style={{ width: '30px', height: '30px' }} />
                                        <p className="mt-2 mb-0">Matching photos...</p>
                                    </div>
                                )}
                            </Panel>

                            <div className="mb-3">
                                <h6>{t('cardGeneration.batch.panels.beneficiariesToCreate', { count: creationData.length })}</h6>
                            </div>
                            <DataTable
                                value={creationData}
                                paginator
                                rows={5}
                                rowsPerPageOptions={[5, 10, 20]}
                                scrollable
                                scrollHeight="400px"
                            >
                                <Column field="firstName" header={t('cardGeneration.batch.table.columns.firstName')} style={{ minWidth: '120px' }} />
                                <Column field="lastName" header={t('cardGeneration.batch.table.columns.lastName')} style={{ minWidth: '120px' }} />
                                <Column field="birthdate" header={t('cardGeneration.batch.table.columns.birthDate')} style={{ minWidth: '120px' }} />
                                <Column field="numeroPolice" header={t('cardGeneration.batch.table.columns.police')} style={{ minWidth: '120px' }} />
                                <Column field="sexe" header={t('cardGeneration.batch.table.columns.gender')} style={{ minWidth: '80px' }} />
                                <Column field="subscriberName" header={t('cardGeneration.batch.table.columns.subscriberName')} style={{ minWidth: '150px' }} />
                                <Column field="mainInsuredName" header={t('cardGeneration.batch.table.columns.mainInsuredName')} style={{ minWidth: '150px' }} />
                                <Column field="temporaryFamilyId" header={t('cardGeneration.batch.table.columns.temporaryFamilyId')} style={{ minWidth: '130px' }} />
                                <Column field="relationshipWithMainInsured" header={t('cardGeneration.batch.table.columns.relationship')} style={{ minWidth: '120px' }} />
                                <Column field="email" header={t('cardGeneration.batch.table.columns.email')} style={{ minWidth: '180px' }} />
                                <Column field="phoneNumber" header={t('cardGeneration.batch.table.columns.phone')} style={{ minWidth: '130px' }} />
                                <Column
                                    header={t('cardGeneration.batch.table.columns.photo')}
                                    body={(rowData) => (
                                        <div className="flex align-items-center gap-2">
                                            {rowData.clientPhoto ? (
                                                <>
                                                    <img
                                                        src={`data:image/jpeg;base64,${rowData.clientPhoto}`}
                                                        alt="Preview"
                                                        style={{
                                                            width: '50px',
                                                            height: '50px',
                                                            objectFit: 'cover',
                                                            borderRadius: '4px',
                                                            border: '1px solid #ddd'
                                                        }}
                                                    />
                                                    <Button
                                                        icon="pi pi-times"
                                                        className="p-button-text p-button-sm p-button-danger"
                                                        onClick={() => {
                                                            this.setState(prevState => ({
                                                                creationData: prevState.creationData.map(ben =>
                                                                    ben.id === rowData.id ? { ...ben, clientPhoto: null } : ben
                                                                )
                                                            }));
                                                        }}
                                                        tooltip={t('cardGeneration.batch.tooltips.deletePhoto')}
                                                    />
                                                </>
                                            ) : (
                                                <FileUpload
                                                    mode="basic"
                                                    name={`photo_${rowData.id}`}
                                                    accept="image/jpeg,image/jpg,image/png"
                                                    maxFileSize={5000000}
                                                    customUpload
                                                    uploadHandler={(e) => this.handleBatchPhotoUpload(e, rowData.id)}
                                                    auto
                                                    chooseLabel={t('cardGeneration.batch.buttons.loadPhoto')}
                                                    className="p-button-sm"
                                                />
                                            )}
                                        </div>
                                    )}
                                />
                            </DataTable>

                            <div className="mt-3">
                                <small className="text-muted">
                                    <i className="pi pi-info-circle mr-2"></i>
                                    {t('cardGeneration.batch.messages.photoOptional')}
                                </small>
                            </div>

                            {/* Create Beneficiaries Button - Moved to bottom for better UX */}
                            <div className="mt-4 text-right">
                                <Button
                                    label={t('cardGeneration.batch.buttons.createBeneficiaries')}
                                    icon="pi pi-check"
                                    className="p-button-success p-button-lg"
                                    onClick={this.createBatchBeneficiaries}
                                    loading={this.state.isGenerating}
                                    disabled={this.state.isGenerating}
                                    style={{ minWidth: '250px' }}
                                />
                            </div>
                        </div>
                    )}

                    {creationValidationErrors.length > 0 && (
                        <div className="mt-3">
                            <h6 className="text-danger">{t('cardGeneration.batch.panels.validationErrors', { count: creationValidationErrors.length })}</h6>
                            <DataTable value={creationValidationErrors}>
                                <Column field="firstName" header={t('cardGeneration.batch.table.columns.firstName')} />
                                <Column field="lastName" header={t('cardGeneration.batch.table.columns.lastName')} />
                                <Column field="temporaryFamilyId" header={t('cardGeneration.batch.table.columns.temporaryFamilyId')} />
                                <Column
                                    field="errors"
                                    header={t('cardGeneration.batch.table.columns.errors')}
                                    body={(rowData) => (
                                        <ul className="mb-0">
                                            {rowData.errors?.map((err, idx) => (
                                                <li key={idx} className="text-danger">{err}</li>
                                            ))}
                                        </ul>
                                    )}
                                />
                            </DataTable>
                        </div>
                    )}
                </Panel>
            </div>
        );
    };

    renderCriteriaSearch = () => {
        const { t } = this.props;
        const { searchCriteria, searchResults, searchLoading } = this.state;

        return (
            <div className="criteria-search">
                <Panel header={t('cardGeneration.batch.panels.searchCriteria')} className="mb-3">
                    <Row>
                        <Col md={3}>
                            <div className="field">
                                <label htmlFor="police">{t('cardGeneration.batch.labels.policeNumber')}</label>
                                <InputText
                                    id="police"
                                    value={searchCriteria.police}
                                    onChange={(e) => this.setState(prev => ({
                                        searchCriteria: { ...prev.searchCriteria, police: e.target.value }
                                    }))}
                                    placeholder={t('cardGeneration.batch.placeholders.policeNumber')}
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="field">
                                <label htmlFor="nom">{t('cardGeneration.batch.labels.lastName')}</label>
                                <InputText
                                    id="nom"
                                    value={searchCriteria.nom}
                                    onChange={(e) => this.setState(prev => ({
                                        searchCriteria: { ...prev.searchCriteria, nom: e.target.value }
                                    }))}
                                    placeholder={t('cardGeneration.batch.placeholders.lastName')}
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="field">
                                <label htmlFor="famille">{t('cardGeneration.batch.labels.familyId')}</label>
                                <InputText
                                    id="famille"
                                    value={searchCriteria.famille}
                                    onChange={(e) => this.setState(prev => ({
                                        searchCriteria: { ...prev.searchCriteria, famille: e.target.value }
                                    }))}
                                    placeholder={t('cardGeneration.batch.placeholders.familyId')}
                                />
                            </div>
                        </Col>
                        <Col md={3}>
                            <div className="field">
                                <label htmlFor="status">{t('cardGeneration.batch.labels.status')}</label>
                                <Dropdown
                                    id="status"
                                    value={searchCriteria.status}
                                    options={this.state.statusOptions}
                                    onChange={(e) => this.setState(prev => ({
                                        searchCriteria: { ...prev.searchCriteria, status: e.value }
                                    }))}
                                    placeholder={t('cardGeneration.batch.placeholders.select')}
                                />
                            </div>
                        </Col>
                    </Row>
                    <Row className="mt-3">
                        <Col>
                            <Button
                                label={t('cardGeneration.batch.buttons.search')}
                                icon="pi pi-search"
                                onClick={this.searchByCriteria}
                                loading={searchLoading}
                                className="p-button-primary"
                            />
                            <Button
                                label={t('cardGeneration.batch.buttons.reset')}
                                icon="pi pi-refresh"
                                onClick={() => this.setState({
                                    searchCriteria: { police: '', nom: '', famille: '', status: 'active' },
                                    searchResults: []
                                })}
                                className="p-button-secondary ml-2"
                            />
                        </Col>
                    </Row>
                </Panel>

                {searchResults.length > 0 && (
                    <Panel header={t('cardGeneration.batch.panels.policeResults', { count: searchResults.length })}>
                        <div className="mb-3">
                            <Button
                                label={t('cardGeneration.batch.buttons.selectAll')}
                                icon="pi pi-check-square"
                                className="p-button-outlined p-button-sm mr-2"
                                onClick={() => this.setState({ selectedBeneficiaries: searchResults })}
                            />
                            <Button
                                label={t('cardGeneration.batch.buttons.deselectAll')}
                                icon="pi pi-minus-circle"
                                className="p-button-outlined p-button-sm mr-2"
                                onClick={() => this.setState({ selectedBeneficiaries: [] })}
                            />
                            <Badge
                                value={`${this.state.selectedBeneficiaries.length} ${t('cardGeneration.batch.labels.selected')}`}
                                severity="info"
                                className="ml-2"
                            />
                        </div>
                        <DataTable
                            value={searchResults}
                            selection={this.state.selectedBeneficiaries}
                            onSelectionChange={(e) => this.setState({ selectedBeneficiaries: e.value })}
                            dataKey="id"
                            paginator
                            rows={10}
                            rowsPerPageOptions={[10, 20, 50]}
                            emptyMessage={t('cardGeneration.batch.table.noResults')}
                        >
                            <Column selectionMode="multiple" headerStyle={{ width: '3em' }} />
                            <Column field="numeroAssure" header={t('cardGeneration.batch.table.columns.insuredNumber')} sortable style={{ minWidth: '120px' }}
                                body={(rowData) => (
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                        <span>{rowData.numeroAssure}</span>
                                        {rowData.hasActiveCard && (
                                            <i
                                                className="pi pi-lock text-red-500"
                                                style={{ fontSize: '0.9rem' }}
                                                title={t('cardGeneration.batch.tooltips.cannotSelect')}
                                            />
                                        )}
                                    </div>
                                )}
                            />
                            <Column field="nom" header={t('cardGeneration.batch.table.columns.lastName')} sortable style={{ minWidth: '150px' }} />
                            <Column field="prenom" header={t('cardGeneration.batch.table.columns.firstName')} sortable style={{ minWidth: '150px' }} />
                            <Column field="police" header={t('cardGeneration.batch.table.columns.police')} sortable style={{ minWidth: '120px' }} />
                            <Column field="famille" header={t('cardGeneration.batch.table.columns.family')} sortable style={{ minWidth: '100px' }} />
                            <Column field="email" header={t('cardGeneration.batch.table.columns.email')} sortable style={{ minWidth: '180px' }} />
                            <Column field="telephone" header={t('cardGeneration.batch.table.columns.phone')} sortable style={{ minWidth: '130px' }} />
                            <Column field="statut" header={t('cardGeneration.batch.table.columns.status')} sortable style={{ minWidth: '100px' }}
                                body={(rowData) => (
                                    <Badge
                                        value={rowData.statut}
                                        severity={rowData.statut === t('cardGeneration.batch.badges.active') ? 'success' : 'warning'}
                                    />
                                )}
                            />
                            <Column header={t('cardGeneration.batch.labels.hasActiveCard')} sortable style={{ minWidth: '120px' }}
                                body={(rowData) => rowData.hasActiveCard ? (
                                    <Badge
                                        value={t('cardGeneration.batch.badges.cardExists')}
                                        severity="danger"
                                        className="text-xs"
                                    />
                                ) : (
                                    <Badge
                                        value={t('cardGeneration.batch.badges.available')}
                                        severity="success"
                                        className="text-xs"
                                    />
                                )}
                            />
                        </DataTable>
                        <div className="mt-3 text-right">
                            <Button
                                label={t('cardGeneration.batch.buttons.addToBatch', { count: this.state.selectedBeneficiaries.length })}
                                icon="pi pi-arrow-right"
                                className="p-button-success"
                                disabled={this.state.selectedBeneficiaries.length === 0}
                                onClick={() => {
                                    this.setState({ activeTabIndex: 1 });
                                    this.showToast('success', t('common.success'), t('cardGeneration.batch.messages.addedToBatch', { count: this.state.selectedBeneficiaries.length }));
                                }}
                            />
                        </div>
                    </Panel>
                )}
            </div>
        );
    };

    renderConfigurationTab = () => {
        const { t } = this.props;
        const { batchConfig, templates, stocks, stocksLoading, selectedBeneficiaries } = this.state;
        const selectedTemplate = templates.find(t => t.id === batchConfig.templateId);

        return (
            <div className="configuration-tab">
                <Row>
                    <Col md={8}>
                        <Card title={t('cardGeneration.batch.panels.batchConfiguration')}>
                            <div className="p-fluid">
                                <Row>
                                    <Col md={6}>
                                        <div className="field">
                                            <label>{t('cardGeneration.batch.labels.batchName')} *</label>
                                            <InputText
                                                value={batchConfig.name}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, name: e.target.value }
                                                }))}
                                                placeholder={t('cardGeneration.batch.placeholders.batchName')}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="field">
                                            <label>{t('cardGeneration.batch.labels.country')} *</label>
                                            <Dropdown
                                                value={batchConfig.country}
                                                options={this.state.countries}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, country: e.value }
                                                }))}
                                                placeholder={t('cardGeneration.batch.placeholders.selectCountry')}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={8}>
                                        <div className="field">
                                            <label>{t('cardGeneration.batch.labels.template')} *</label>
                                            <Dropdown
                                                value={batchConfig.templateId}
                                                options={templates.map(t => {
                                                    const templateId = t.id || t.Id;
                                                    const templateName = t.templateName || t.TemplateName ||
                                                                        t.name || t.Name || `Template ${templateId}`;
                                                    return {
                                                        label: templateName,
                                                        value: templateId
                                                    };
                                                })}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, templateId: e.value }
                                                }))}
                                                placeholder={t('cardGeneration.batch.placeholders.selectTemplate')}
                                            />
                                        </div>
                                    </Col>
                                    <Col md={4}>
                                        <div className="field">
                                            <label>&nbsp;</label>
                                            <Button
                                                label={t('cardGeneration.batch.buttons.preview')}
                                                icon="pi pi-eye"
                                                className="p-button-outlined w-full"
                                                disabled={!batchConfig.templateId}
                                                onClick={this.handleTemplatePreview}
                                                loading={this.state.previewLoading}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12}>
                                        <div className="field">
                                            <label>{t('cardGeneration.batch.labels.cardStock')} *</label>
                                            <Dropdown
                                                value={batchConfig.stockId}
                                                options={stocks.map(s => {
                                                    const stockId = s.id || s.Id;
                                                    const stockName = s.productName || s.ProductName || `Stock ${stockId}`;
                                                    const available = s.currentQuantity || s.CurrentQuantity ||
                                                                     s.quantityAvailable || s.QuantityAvailable || 0;
                                                    return {
                                                        label: `${stockName} (${available} ${t('cardGeneration.batch.labels.available')})`,
                                                        value: stockId
                                                    };
                                                })}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, stockId: e.value }
                                                }))}
                                                placeholder={t('cardGeneration.batch.placeholders.selectStock')}
                                                disabled={stocksLoading}
                                            />
                                            {stocksLoading && <small className="text-muted">{t('cardGeneration.batch.messages.loadingStocks')}</small>}
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={6}>
                                        <div className="field">
                                            <label>{t('cardGeneration.batch.labels.priority')}</label>
                                            <Dropdown
                                                value={batchConfig.priority}
                                                options={this.state.priorities}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, priority: e.value }
                                                }))}
                                            />
                                        </div>
                                    </Col>
                                </Row>
                                <div className="field">
                                    <label>{t('cardGeneration.batch.labels.description')}</label>
                                    <InputTextarea
                                        value={batchConfig.description}
                                        onChange={(e) => this.setState(prev => ({
                                            batchConfig: { ...prev.batchConfig, description: e.target.value }
                                        }))}
                                        rows={3}
                                        placeholder={t('cardGeneration.batch.placeholders.description')}
                                    />
                                </div>
                                <Row>
                                    <Col md={6}>
                                        <div className="field-checkbox">
                                            <Checkbox
                                                id="generatePdf"
                                                checked={batchConfig.generatePdf}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, generatePdf: e.checked }
                                                }))}
                                            />
                                            <label htmlFor="generatePdf">{t('cardGeneration.batch.labels.generateGroupedPdf')}</label>
                                        </div>
                                    </Col>
                                    <Col md={6}>
                                        <div className="field-checkbox">
                                            <Checkbox
                                                id="autoValidate"
                                                checked={batchConfig.autoValidate}
                                                onChange={(e) => this.setState(prev => ({
                                                    batchConfig: { ...prev.batchConfig, autoValidate: e.checked }
                                                }))}
                                            />
                                            <label htmlFor="autoValidate">{t('cardGeneration.batch.labels.autoValidate')}</label>
                                        </div>
                                    </Col>
                                </Row>

                                <div className="mt-4">
                                    <Button
                                        label={t('cardGeneration.batch.buttons.generateBatch')}
                                        icon="pi pi-play"
                                        className="p-button-success"
                                        onClick={this.generateBatch}
                                        disabled={selectedBeneficiaries.length === 0}
                                        loading={this.state.isGenerating}
                                    />
                                </div>
                            </div>
                        </Card>
                    </Col>

                    <Col md={4}>
                        <Card title={t('cardGeneration.batch.panels.batchSummary')} className="sticky-card">
                            <div className="summary-content">
                                <div className="summary-item mb-3">
                                    <label className="text-muted">{t('cardGeneration.batch.labels.beneficiaries')}</label>
                                    <div className="text-center">
                                        <h2 className="text-primary mb-0">{selectedBeneficiaries.length}</h2>
                                        <small className="text-muted">{t('cardGeneration.batch.labels.selected')}</small>
                                    </div>
                                </div>

                                {batchConfig.templateId && selectedTemplate && (
                                    <div className="summary-item mb-3">
                                        <label className="text-muted">{t('cardGeneration.batch.labels.template')}</label>
                                        <div className="p-2 border-round surface-100">
                                            <strong>{selectedTemplate.name}</strong>
                                            <br />
                                            <small className="text-muted">{selectedTemplate.type || 'Standard'}</small>
                                        </div>
                                    </div>
                                )}

                                {batchConfig.country && (
                                    <div className="summary-item mb-3">
                                        <label className="text-muted">{t('cardGeneration.batch.labels.country')}</label>
                                        <div className="p-2 border-round surface-100">
                                            <strong>{this.state.countries.find(c => c.value === batchConfig.country)?.label}</strong>
                                        </div>
                                    </div>
                                )}

                                {batchConfig.name && (
                                    <div className="summary-item mb-3">
                                        <label className="text-muted">{t('cardGeneration.batch.labels.batchName')}</label>
                                        <div className="p-2 border-round surface-100">
                                            {batchConfig.name}
                                        </div>
                                    </div>
                                )}

                                <div className="summary-item">
                                    <label className="text-muted">{t('cardGeneration.batch.options.title')}</label>
                                    <div className="p-2 border-round surface-100">
                                        {batchConfig.generatePdf && (
                                            <div className="mb-1">
                                                <i className="pi pi-check text-green-500 mr-2"></i>
                                                {t('cardGeneration.batch.labels.generateGroupedPdf')}
                                            </div>
                                        )}
                                        {batchConfig.autoValidate && (
                                            <div>
                                                <i className="pi pi-check text-green-500 mr-2"></i>
                                                {t('cardGeneration.batch.labels.autoValidate')}
                                            </div>
                                        )}
                                        {!batchConfig.generatePdf && !batchConfig.autoValidate && (
                                            <small className="text-muted">{t('common.none')}</small>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Card>

                        {selectedBeneficiaries.length > 0 && (
                            <Card title={t('cardGeneration.batch.labels.beneficiaries')} className="mt-3">
                                <div className="beneficiaries-preview" style={{ maxHeight: '300px', overflowY: 'auto' }}>
                                    {selectedBeneficiaries.slice(0, 5).map((ben, index) => (
                                        <div key={index} className="p-2 mb-2 border-round surface-100">
                                            <div className="font-semibold">{ben.nom} {ben.prenom}</div>
                                            <small className="text-muted">{ben.numeroAssure}</small>
                                        </div>
                                    ))}
                                    {selectedBeneficiaries.length > 5 && (
                                        <div className="text-center text-muted">
                                            <small>+{selectedBeneficiaries.length - 5} {t('common.others')}...</small>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        )}
                    </Col>
                </Row>
            </div>
        );
    };

    renderGenerationTab = () => {
        const { t } = this.props;
        const { isGenerating, generationProgress, generationStats, currentBatch } = this.state;

        return (
            <Card title={t('cardGeneration.batch.panels.generationInProgress')}>
                {isGenerating ? (
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="pi pi-spin pi-spinner text-6xl text-primary"></i>
                        </div>
                        <h5>{t('cardGeneration.batch.messages.generatingBatch')}</h5>
                        <ProgressBar value={generationProgress} className="mt-3" />
                        <p className="text-muted mt-2">{t('cardGeneration.batch.messages.processingCards', { count: generationStats.total })}</p>

                        <Row className="mt-4">
                            <Col md={3}>
                                <div className="text-center">
                                    <h4 className="text-primary">{generationStats.total}</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.labels.total')}</p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="text-center">
                                    <h4 className="text-success">{generationStats.success}</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.labels.success')}</p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="text-center">
                                    <h4 className="text-danger">{generationStats.errors}</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.labels.errors')}</p>
                                </div>
                            </Col>
                            <Col md={3}>
                                <div className="text-center">
                                    <h4 className="text-warning">{generationStats.inProgress}</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.labels.inProgress')}</p>
                                </div>
                            </Col>
                        </Row>
                    </div>
                ) : currentBatch ? (
                    <div className="text-center">
                        <div className="mb-4">
                            <i className="pi pi-check-circle text-6xl text-green-500"></i>
                        </div>
                        <h5>{t('cardGeneration.batch.messages.batchSuccess')}</h5>
                        <div className="mt-4">
                            <Badge value={`ID: ${currentBatch.id}`} className="mr-2" />
                            <Badge value={`${t('cardGeneration.batch.labels.status')}: ${currentBatch.status}`} severity="info" />
                        </div>

                        <Row className="mt-4">
                            <Col md={4}>
                                <div className="text-center">
                                    <h4 className="text-success">{generationStats.success}</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.messages.cardsGenerated')}</p>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center">
                                    <h4 className="text-danger">{generationStats.errors}</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.labels.errors')}</p>
                                </div>
                            </Col>
                            <Col md={4}>
                                <div className="text-center">
                                    <h4 className="text-primary">{Math.round((generationStats.success / generationStats.total) * 100)}%</h4>
                                    <p className="text-muted">{t('cardGeneration.batch.messages.successRate')}</p>
                                </div>
                            </Col>
                        </Row>

                        <div className="mt-4">
                            <Button
                                label={t('cardGeneration.batch.buttons.downloadPdf')}
                                icon="pi pi-download"
                                className="mr-2"
                                onClick={() => this.downloadBatchPdf(currentBatch)}
                                loading={this.state.isPdfDownloading}
                                disabled={this.state.isPdfDownloading}
                            />
                            {generationStats.errors > 0 && (
                                <Button
                                    label={t('cardGeneration.batch.buttons.retryFailed')}
                                    icon="pi pi-refresh"
                                    className="p-button-warning mr-2"
                                    onClick={() => this.retryFailedCards(currentBatch)}
                                />
                            )}
                            <Button
                                label={t('cardGeneration.batch.buttons.newBatch')}
                                icon="pi pi-plus"
                                className="p-button-outlined"
                                onClick={() => {
                                    this.setState({
                                        activeTabIndex: 0,
                                        currentBatch: null,
                                        selectedBeneficiaries: [],
                                        batchConfig: {
                                            name: '',
                                            description: '',
                                            templateId: null,
                                            priority: 'Normal',
                                            country: '',
                                            generatePdf: true,
                                            autoValidate: false
                                        }
                                    });
                                }}
                            />
                        </div>

                        {/* Progress bar pour le téléchargement PDF */}
                        {this.state.isPdfDownloading && (
                            <div className="mt-3">
                                <small className="text-muted">{t('cardGeneration.batch.messages.downloadingPdf')}</small>
                                <ProgressBar value={this.state.downloadProgress} className="mt-2" />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center text-muted">
                        <i className="pi pi-info-circle text-4xl mb-3"></i>
                        <p>{t('cardGeneration.batch.messages.noGeneration')}</p>
                    </div>
                )}
            </Card>
        );
    };

    renderHistoryTab = () => {
        const { t } = this.props;
        const { batchHistory, historyLoading } = this.state;

        const statusBodyTemplate = (rowData) => {
            const config = {
                'completed': { severity: 'success', label: t('cardGeneration.batch.badges.completed') },
                'inprogress': { severity: 'warning', label: t('cardGeneration.batch.badges.inProgress') },
                'in_progress': { severity: 'warning', label: t('cardGeneration.batch.badges.inProgress') },
                'failed': { severity: 'danger', label: t('cardGeneration.batch.badges.failed') },
                'pending': { severity: 'info', label: t('cardGeneration.batch.badges.pending') },
                'ready': { severity: 'info', label: t('cardGeneration.batch.badges.ready') },
                'cancelled': { severity: 'secondary', label: t('cardGeneration.batch.badges.cancelled') }
            };
            const statusLower = (rowData.status || '').toLowerCase().replace(/\s+/g, '_');
            const statusConfig = config[statusLower] || { severity: 'secondary', label: rowData.status || t('common.unknown') };
            return <Badge value={statusConfig.label} severity={statusConfig.severity} />;
        };

        const actionsBodyTemplate = (rowData) => {
            const items = [
                {
                    label: t('cardGeneration.batch.buttons.downloadPdf'),
                    icon: 'pi pi-download',
                    command: () => this.downloadBatchPdf(rowData)
                },
                {
                    label: t('common.viewDetails'),
                    icon: 'pi pi-eye'
                },
                {
                    separator: true
                },
                {
                    label: t('common.retry'),
                    icon: 'pi pi-refresh',
                    command: () => this.retryFailedCards(rowData)
                }
            ];

            return (
                <SplitButton
                    icon="pi pi-ellipsis-v"
                    className="p-button-rounded p-button-text"
                    model={items}
                />
            );
        };

        return (
            <Card title={t('cardGeneration.batch.panels.batchHistory')}>
                <DataTable
                    value={batchHistory}
                    loading={historyLoading}
                    paginator
                    rows={10}
                    rowsPerPageOptions={[5, 10, 25]}
                    emptyMessage={t('cardGeneration.batch.messages.noBatchFound')}
                >
                    <Column field="name" header={t('cardGeneration.batch.table.columns.batchName')} sortable />
                    <Column field="country" header={t('cardGeneration.batch.table.columns.country')} sortable />
                    <Column field="totalCards" header={t('cardGeneration.batch.table.columns.totalCards')} sortable />
                    <Column field="successCards" header={t('cardGeneration.batch.table.columns.successCards')} sortable />
                    <Column field="errorCards" header={t('cardGeneration.batch.table.columns.errorCards')} sortable />
                    <Column field="status" header={t('cardGeneration.batch.table.columns.status')} body={statusBodyTemplate} sortable />
                    <Column
                        field="createdAt"
                        header={t('cardGeneration.batch.table.columns.createdAt')}
                        body={(rowData) => new Date(rowData.createdAt).toLocaleDateString()}
                        sortable
                    />
                    <Column body={actionsBodyTemplate} header={t('cardGeneration.batch.table.columns.actions')} style={{ width: '100px' }} />
                </DataTable>
            </Card>
        );
    };

    renderPhotoMatchingDialog = () => {
        const { photoMatchingResults, showPhotoMatchingDialog } = this.state;
        const { matched, unmatched, orphanPhotos } = photoMatchingResults;

        const hasIssues = orphanPhotos.length > 0 || unmatched.length > 0;

        return (
            <Dialog
                header={
                    <div className="flex align-items-center gap-2">
                        {hasIssues ? '⚠️' : '✅'} Photo Matching Results
                    </div>
                }
                visible={showPhotoMatchingDialog}
                style={{ width: hasIssues ? '80vw' : '50vw' }}
                onHide={() => this.setState({ showPhotoMatchingDialog: false })}
                footer={
                    <div>
                        <Button
                            label="Cancel"
                            icon="pi pi-times"
                            onClick={() => this.setState({ showPhotoMatchingDialog: false })}
                            className="p-button-text"
                        />
                        <Button
                            label={hasIssues ? `Apply ${matched.length} Photos` : `Apply All ${matched.length} Photos`}
                            icon="pi pi-check"
                            onClick={this.applyPhotoMatching}
                            className="p-button-success"
                            disabled={matched.length === 0}
                        />
                    </div>
                }
            >
                <div className="photo-matching-results">
                    {/* Summary Banner */}
                    <div className="mb-3 p-3" style={{
                        backgroundColor: hasIssues ? '#fff7ed' : '#f0fdf4',
                        borderLeft: `4px solid ${hasIssues ? '#f97316' : '#22c55e'}`,
                        borderRadius: '4px'
                    }}>
                        <div className="flex align-items-center gap-3">
                            <div>
                                <h5 className="mb-1">{matched.length} photos matched successfully</h5>
                                {hasIssues && (
                                    <p className="mb-0 text-muted">
                                        {orphanPhotos.length > 0 && `${orphanPhotos.length} unmatched photos`}
                                        {orphanPhotos.length > 0 && unmatched.length > 0 && ', '}
                                        {unmatched.length > 0 && `${unmatched.length} beneficiaries without photos`}
                                    </p>
                                )}
                                {!hasIssues && (
                                    <p className="mb-0 text-muted">All photos matched! Ready to apply.</p>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Only show details if there are issues */}
                    {hasIssues && (
                        <>
                            {/* Matched Photos - Collapsed by default if all OK */}
                            {matched.length > 0 && (
                                <Panel header={`✅ Matched (${matched.length})`} className="mb-3" toggleable collapsed>
                                    <DataTable value={matched} paginator rows={5} scrollable scrollHeight="250px">
                                        <Column
                                            header="Photo"
                                            body={(rowData) => (
                                                <img
                                                    src={`data:image/jpeg;base64,${rowData.base64}`}
                                                    alt={rowData.photo.name}
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                />
                                            )}
                                            style={{ width: '70px' }}
                                        />
                                        <Column
                                            body={(rowData) => (
                                                <div>
                                                    <div style={{ fontWeight: '500' }}>{rowData.beneficiary.firstName} {rowData.beneficiary.lastName}</div>
                                                    <small className="text-muted">{rowData.photo.name}</small>
                                                </div>
                                            )}
                                        />
                                    </DataTable>
                                </Panel>
                            )}

                            {/* Unmatched Beneficiaries */}
                            {unmatched.length > 0 && (
                                <Panel header={`⚠️ Beneficiaries Without Photos (${unmatched.length})`} className="mb-3" toggleable>
                                    <DataTable value={unmatched} paginator rows={5}>
                                        <Column field="numeroAssurer" header="Insured Number" />
                                        <Column
                                            header="Name"
                                            body={(rowData) => `${rowData.firstName} ${rowData.lastName}`}
                                        />
                                    </DataTable>
                                </Panel>
                            )}

                            {/* Orphan Photos - Manual Assignment */}
                            {orphanPhotos.length > 0 && (
                                <Panel header={`❌ Unmatched Photos (${orphanPhotos.length}) - Manual Assignment Required`} className="mb-3" toggleable>
                                    <DataTable value={orphanPhotos} paginator rows={5}>
                                        <Column
                                            header="Photo"
                                            body={(rowData) => (
                                                <img
                                                    src={URL.createObjectURL(rowData)}
                                                    alt={rowData.name}
                                                    style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                                />
                                            )}
                                            style={{ width: '70px' }}
                                        />
                                        <Column field="name" header="File Name" />
                                        <Column
                                            header="Assign To Beneficiary"
                                            body={(rowData) => (
                                                <Dropdown
                                                    options={unmatched}
                                                    optionLabel={(option) => `${option.numeroAssurer} - ${option.firstName} ${option.lastName}`}
                                                    placeholder="Select beneficiary"
                                                    onChange={(e) => this.assignOrphanPhoto(rowData, e.value.id)}
                                                    style={{ width: '100%' }}
                                                />
                                            )}
                                        />
                                    </DataTable>
                                </Panel>
                            )}
                        </>
                    )}
                </div>
            </Dialog>
        );
    };

    render() {
        const { t } = this.props;
        const { activeTabIndex, showValidationDialog, selectedBeneficiaries, batchConfig } = this.state;

        return (
            <div className="generation-batch">
                <Toast ref={this.toast} />

                <TabView activeIndex={activeTabIndex} onTabChange={(e) => this.setState({ activeTabIndex: e.index })}>
                    <TabPanel header={t('cardGeneration.batch.tabs.import')} leftIcon="pi pi-upload">
                        {this.renderImportTab()}
                    </TabPanel>

                    <TabPanel header={t('cardGeneration.batch.tabs.configuration')} leftIcon="pi pi-cog">
                        {this.renderConfigurationTab()}
                    </TabPanel>

                    <TabPanel header={t('cardGeneration.batch.tabs.generation')} leftIcon="pi pi-play">
                        {this.renderGenerationTab()}
                    </TabPanel>

                    <TabPanel header={t('cardGeneration.batch.tabs.history')} leftIcon="pi pi-history">
                        {this.renderHistoryTab()}
                    </TabPanel>
                </TabView>

                {/* Photo Matching Dialog */}
                {this.renderPhotoMatchingDialog()}

                {/* Dialog de validation */}
                <Dialog
                    visible={showValidationDialog}
                    style={{ width: '600px' }}
                    header={t('cardGeneration.batch.confirmation.title')}
                    modal
                    onHide={() => this.setState({ showValidationDialog: false })}
                    footer={
                        <div>
                            <Button
                                label={t('cardGeneration.batch.buttons.cancel')}
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => this.setState({ showValidationDialog: false })}
                            />
                            <Button
                                label={t('cardGeneration.batch.buttons.confirm')}
                                icon="pi pi-check"
                                onClick={this.confirmBatchGeneration}
                            />
                        </div>
                    }
                >
                    <div className="confirmation-content">
                        <div className="mb-3">
                            <i className="pi pi-exclamation-triangle text-orange-500 text-2xl mr-2"></i>
                            <span>{t('cardGeneration.batch.confirmation.message')}</span>
                        </div>
                        <div className="confirmation-details">
                            <p><strong>{t('cardGeneration.batch.confirmation.batchName')}</strong> {batchConfig.name}</p>
                            <p><strong>{t('cardGeneration.batch.confirmation.cardCount')}</strong> {selectedBeneficiaries.length}</p>
                            <p><strong>{t('cardGeneration.batch.confirmation.template')}</strong> {this.state.templates.find(t => t.id === batchConfig.templateId)?.name}</p>
                            <p><strong>{t('cardGeneration.batch.confirmation.priority')}</strong> {batchConfig.priority}</p>
                            <p><strong>{t('cardGeneration.batch.confirmation.pdfGeneration')}</strong> {batchConfig.generatePdf ? t('common.yes') : t('common.no')}</p>
                        </div>
                    </div>
                </Dialog>

                {/* Dialog de prévisualisation du template */}
                <Dialog
                    visible={this.state.showPreviewDialog}
                    style={{ width: '900px' }}
                    header={t('cardGeneration.batch.preview.title')}
                    modal
                    onHide={() => this.setState({ showPreviewDialog: false, previewUrl: null })}
                    footer={
                        <div>
                            <Button
                                label={t('cardGeneration.batch.buttons.close')}
                                icon="pi pi-times"
                                className="p-button-text"
                                onClick={() => this.setState({ showPreviewDialog: false, previewUrl: null })}
                            />
                        </div>
                    }
                >
                    <div className="template-preview-content">
                        {batchConfig.templateId && (
                            <>
                                <div className="mb-3 p-3 border-round surface-100">
                                    <Row>
                                        <Col md={6}>
                                            <strong>{t('cardGeneration.batch.labels.template')}:</strong> {this.state.templates.find(t => t.id === batchConfig.templateId)?.name}
                                        </Col>
                                        <Col md={6}>
                                            <strong>{t('cardGeneration.batch.labels.country')}:</strong> {this.state.countries.find(c => c.value === batchConfig.country)?.label || t('common.notSelected')}
                                        </Col>
                                    </Row>
                                </div>

                                <div className="template-preview-wrapper text-center">
                                    {this.state.previewLoading ? (
                                        <div className="flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                                            <ProgressSpinner />
                                        </div>
                                    ) : this.state.previewUrl ? (
                                        <div style={{
                                            border: '2px dashed #ccc',
                                            borderRadius: '8px',
                                            padding: '40px',
                                            backgroundColor: '#f8f9fa',
                                            minHeight: '400px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <img
                                                src={this.state.previewUrl}
                                                alt={t('cardGeneration.individual.labels.cardPreview')}
                                                style={{
                                                    maxWidth: '100%',
                                                    maxHeight: '400px',
                                                    objectFit: 'contain',
                                                    borderRadius: '8px',
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        <div style={{
                                            border: '2px dashed #ccc',
                                            borderRadius: '8px',
                                            padding: '40px',
                                            backgroundColor: '#f8f9fa',
                                            minHeight: '400px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}>
                                            <div className="text-muted">
                                                <i className="pi pi-image text-4xl mb-3"></i>
                                                <p>{t('cardGeneration.batch.messages.noPreview')}</p>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </Dialog>
            </div>
        );
    }
}

export default withTranslation()(GenerationBatch);